import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Chrome } from "lucide-react";

const AuthPage = () => {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [loginState, setLoginState] = useState({ email: "", password: "" });
  const [signupState, setSignupState] = useState({
    email: "",
    password: "",
    username: "",
    full_name: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLink, setResetLink] = useState("");

  const handleLogin = async (event) => {
    event.preventDefault();
    try {
      await login(loginState.email, loginState.password);
      navigate("/");
    } catch (error) {
      toast.error("Login failed. Check your email and password.");
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (!signupState.username.trim() || !signupState.full_name.trim()) {
      toast.error("Username and full name are required.");
      return;
    }
    try {
      await signup(signupState);
      navigate("/");
    } catch (error) {
      toast.error("Signup failed. Please try a different email or username.");
    }
  };

  const handleGoogleLogin = () => {
    // Do not hardcode redirect URL; always derive from current origin.
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordReset = async (event) => {
    event.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Enter the email tied to your account.");
      return;
    }
    try {
      const response = await api.post("/auth/forgot-password", { email: resetEmail.trim() });
      const mocked = response.data.mocked;
      const baseMessage = response.data.message || "Check your inbox for reset instructions.";
      setResetMessage(mocked ? `MOCKED: ${baseMessage}` : baseMessage);
      if (response.data.mock_reset_token) {
        const link = `${window.location.origin}/reset-password?token=${response.data.mock_reset_token}`;
        setResetLink(link);
      } else {
        setResetLink("");
      }
    } catch (error) {
      toast.error("Unable to start reset flow.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-semibold" data-testid="auth-page-title">
            Yopinion
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="auth-page-subtitle">
            Sign in to vote, comment, and publish polls.
          </p>
        </div>
        <div className="space-y-3" data-testid="auth-google-section">
          <Button
            variant="secondary"
            className="w-full"
            onClick={handleGoogleLogin}
            data-testid="auth-google-button"
          >
            <Chrome className="mr-2 h-4 w-4" />
            Continue with Google
          </Button>
          <div className="text-center text-xs uppercase text-muted-foreground" data-testid="auth-divider">
            or use email
          </div>
        </div>
        <Tabs defaultValue="login" className="w-full" data-testid="auth-tabs">
          <TabsList className="grid w-full grid-cols-2" data-testid="auth-tabs-list">
            <TabsTrigger value="login" data-testid="auth-tab-login">
              Login
            </TabsTrigger>
            <TabsTrigger value="signup" data-testid="auth-tab-signup">
              Sign up
            </TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="mt-4" data-testid="auth-login-panel">
            <form className="space-y-4" onSubmit={handleLogin}>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="login-email-label">
                  Email
                </label>
                <Input
                  type="email"
                  value={loginState.email}
                  onChange={(event) =>
                    setLoginState({ ...loginState, email: event.target.value })
                  }
                  data-testid="login-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="login-password-label">
                  Password
                </label>
                <Input
                  type={showLoginPassword ? "text" : "password"}
                  value={loginState.password}
                  onChange={(event) =>
                    setLoginState({ ...loginState, password: event.target.value })
                  }
                  data-testid="login-password-input"
                  required
                />
              </div>
              <div className="flex items-center justify-between text-xs" data-testid="login-password-toggle-row">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={showLoginPassword}
                    onCheckedChange={setShowLoginPassword}
                    data-testid="login-show-password-toggle"
                  />
                  <span data-testid="login-show-password-label">Show password</span>
                </div>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setResetOpen(true)}
                  data-testid="login-forgot-password-button"
                >
                  Forgot password?
                </button>
              </div>
              <Button className="w-full" type="submit" data-testid="login-submit-button">
                Sign in
              </Button>
            </form>
          </TabsContent>
          <TabsContent value="signup" className="mt-4" data-testid="auth-signup-panel">
            <form className="space-y-4" onSubmit={handleSignup}>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="signup-username-label">
                  Username
                </label>
                <Input
                  value={signupState.username}
                  onChange={(event) =>
                    setSignupState({ ...signupState, username: event.target.value })
                  }
                  data-testid="signup-username-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="signup-name-label">
                  Full name
                </label>
                <Input
                  value={signupState.full_name}
                  onChange={(event) =>
                    setSignupState({ ...signupState, full_name: event.target.value })
                  }
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="signup-email-label">
                  Email
                </label>
                <Input
                  type="email"
                  value={signupState.email}
                  onChange={(event) =>
                    setSignupState({ ...signupState, email: event.target.value })
                  }
                  data-testid="signup-email-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" data-testid="signup-password-label">
                  Password
                </label>
                <Input
                  type={showSignupPassword ? "text" : "password"}
                  value={signupState.password}
                  onChange={(event) =>
                    setSignupState({ ...signupState, password: event.target.value })
                  }
                  data-testid="signup-password-input"
                  required
                />
              </div>
              <div className="flex items-center gap-2 text-xs" data-testid="signup-password-toggle-row">
                <Switch
                  checked={showSignupPassword}
                  onCheckedChange={setShowSignupPassword}
                  data-testid="signup-show-password-toggle"
                />
                <span data-testid="signup-show-password-label">Show password</span>
              </div>
              <Button className="w-full" type="submit" data-testid="signup-submit-button">
                Create account
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle data-testid="forgot-password-title">Reset your password</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handlePasswordReset} data-testid="forgot-password-form">
            <div className="space-y-2">
              <label className="text-sm font-medium" data-testid="forgot-password-email-label">
                Email
              </label>
              <Input
                type="email"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                data-testid="forgot-password-email-input"
                required
              />
            </div>
            <Button type="submit" data-testid="forgot-password-submit">
              Send reset link
            </Button>
            {resetMessage && (
              <div className="text-xs text-muted-foreground" data-testid="forgot-password-message">
                {resetMessage}
              </div>
            )}
            {resetLink && (
              <div className="text-xs" data-testid="forgot-password-mock-link">
                <span className="font-semibold text-rose-600">MOCKED:</span> Use this reset link: {" "}
                <a href={resetLink} className="underline" data-testid="forgot-password-link">
                  {resetLink}
                </a>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuthPage;
